import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpCircle, ArrowDownCircle, Loader2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

interface QuickEntrySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: {
    description: string;
    amount: number;
    date: string;
    type: "income" | "expense";
    readonly?: boolean;
  };
  onSave: (data: {
    type: "receivable" | "payable";
    description: string;
    amount: number;
    due_date: string;
    category_id: string | null;
    cost_center_id: string | null;
    deal_id: string | null;
  }) => Promise<void>;
}

export function QuickEntrySheet({ open, onOpenChange, prefill, onSave }: QuickEntrySheetProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const [type, setType] = useState<"income" | "expense">("expense");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [costCenterId, setCostCenterId] = useState<string>("");
  const [dealId, setDealId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const isReadonly = prefill?.readonly === true;

  // Reset on open with prefill
  useEffect(() => {
    if (open && prefill) {
      setType(prefill.type);
      setDescription(prefill.description);
      setAmount(Math.abs(prefill.amount));
      setDueDate(prefill.date);
      setCategoryId("");
      setCostCenterId("");
      setDealId("");
    }
  }, [open, prefill]);

  const { data: categories = [] } = useQuery({
    queryKey: ["financial-categories", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase.from("financial_categories").select("*").eq("tenant_id", tenantId!).order("code");
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: ["cost-centers", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase.from("cost_centers").select("*").eq("tenant_id", tenantId!).order("code");
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  const { data: deals = [] } = useQuery({
    queryKey: ["deals-list", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase.from("deals").select("id, code, name, lead:leads(name)").eq("tenant_id", tenantId!).eq("status", "open").order("code", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  const filteredCategories = categories.filter((c: any) =>
    type === "income" ? c.type === "income" : c.type === "expense"
  );

  const handleSave = async () => {
    if (!description.trim() || !amount || !dueDate) return;
    setSaving(true);
    try {
      await onSave({
        type: type === "income" ? "receivable" : "payable",
        description: description.trim(),
        amount,
        due_date: dueDate,
        category_id: categoryId || null,
        cost_center_id: costCenterId || null,
        deal_id: dealId || null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {type === "income" ? <ArrowUpCircle className="h-5 w-5 text-emerald-500" /> : <ArrowDownCircle className="h-5 w-5 text-rose-500" />}
            Criar Lançamento Rápido
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Type toggle */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant={type === "income" ? "default" : "outline"}
              size="sm"
              className={cn("flex-1 gap-1.5", type === "income" && "bg-emerald-600 hover:bg-emerald-700")}
              onClick={() => !isReadonly && setType("income")}
              disabled={isReadonly}
            >
              <ArrowUpCircle className="h-3.5 w-3.5" /> Receita
              {isReadonly && type === "income" && <Lock className="h-3 w-3 ml-1" />}
            </Button>
            <Button
              type="button"
              variant={type === "expense" ? "default" : "outline"}
              size="sm"
              className={cn("flex-1 gap-1.5", type === "expense" && "bg-rose-600 hover:bg-rose-700")}
              onClick={() => !isReadonly && setType("expense")}
              disabled={isReadonly}
            >
              <ArrowDownCircle className="h-3.5 w-3.5" /> Despesa
              {isReadonly && type === "expense" && <Lock className="h-3 w-3 ml-1" />}
            </Button>
          </div>

          {/* Amount highlight */}
          <div className="rounded-xl border border-border/50 bg-secondary/30 p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Valor do Extrato</p>
            <p className={cn("text-2xl font-bold font-mono", type === "income" ? "text-emerald-500" : "text-rose-500")}>
              {formatCurrency(amount)}
            </p>
            {isReadonly && <p className="text-[10px] text-muted-foreground mt-1 flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> Valor herdado do extrato</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} readOnly={isReadonly} className={cn(isReadonly && "bg-muted cursor-not-allowed")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Valor</Label>
              <Input type="number" step="0.01" value={amount || ""} onChange={e => setAmount(parseFloat(e.target.value) || 0)} readOnly={isReadonly} className={cn(isReadonly && "bg-muted cursor-not-allowed")} />
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} readOnly={isReadonly} className={cn(isReadonly && "bg-muted cursor-not-allowed")} />
            </div>
          </div>

          <hr className="border-border/30" />
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Classificação</p>

          <div className="space-y-1.5">
            <Label>Plano de Contas</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {filteredCategories.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="font-mono text-muted-foreground mr-1">{c.code}</span> {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Centro de Custo</Label>
            <Select value={costCenterId} onValueChange={setCostCenterId}>
              <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
              <SelectContent>
                {costCenters.map((cc: any) => (
                  <SelectItem key={cc.id} value={cc.id}>
                    <span className="font-mono text-muted-foreground mr-1">{cc.code}</span> {cc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Oportunidade</Label>
            <Select value={dealId} onValueChange={setDealId}>
              <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
              <SelectContent>
                {deals.map((d: any) => (
                  <SelectItem key={d.id} value={d.id}>
                    #{d.code} — {d.name || (d.lead as any)?.name || "Sem nome"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <SheetFooter>
          <Button onClick={handleSave} disabled={saving || !description.trim() || !amount || !dueDate} className="w-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Criar e Conciliar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
